import { Group, ScrollArea, Stack, Textarea, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { UseFormReturnType } from '@mantine/form';
import { useState } from 'react';
import { RelativeBusinessValueUnits } from '../../../api/initiative-client/initiative-client.type';
import { useTranslation } from 'react-i18next';
import { getCurrency } from '../../../helpers/currency-helpers';
import { STANDARD_DATE_FORMATS } from '../../../helpers/date-time-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { baseWhite, inkLight, redBase, skyDark } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Radio } from '../../../ui-library/radio/radio';
import { Text } from '../../../ui-library/typography/typography';
import { StrategyEntityType } from '../strategy.type';
import { StrategyEntityDeleteModal } from './strategy-entity-delete-modal';
import { commonInputProps } from './strategy-entity-modal.helpers';
import classes from './strategy-entity-modal.module.css';
import type { StrategyEntityModalMode } from './strategy-entity-modal.type';

type Props = {
  mode: StrategyEntityModalMode;
  entityType: StrategyEntityType;
  form: UseFormReturnType<any>;
  onNext: (data: any) => void;
  onCancel: () => void;
  onDelete?: (...args: any[]) => Promise<void>;
  isDeleting?: boolean;
};

export function StepOne({ mode, entityType, form, onNext, onCancel, onDelete, isDeleting = false }: Props) {
  const i18n = useTranslation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const organization = useGlobalStore((state) => state.organization);
  const { name: currencyName } = getCurrency(organization);

  const handleDeleteConfirm = async () => {
    await onDelete?.();
    setIsDeleteModalOpen(false);
  };

  const getEntityTitle = () => {
    if (entityType === 'initiative') return i18n.t('common.initiative');
    if (entityType === 'outcome') return i18n.t('common.outcome');
    return i18n.t('common.strategic_goal');
  };

  const getLabels = () => {
    const entityName = getEntityTitle();
    // Get plural form for description (e.g., "Initiatives", "Outcomes", "Strategic Goals")
    const entityPlural =
      entityType === 'initiative'
        ? i18n.t('common.initiatives')
        : entityType === 'outcome'
          ? i18n.t('common.outcomes')
          : i18n.t('common.strategic_goals');
    return {
      formDescription: i18n.t('strategy.initiatives.form.description', { entity: entityPlural }),
      nameLabel: i18n.t('strategy.initiatives.form.name_label'),
      namePlaceholder: i18n.t('strategy.initiatives.form.name_placeholder', { entity: entityName.toLowerCase() }),
      descriptionLabel: i18n.t('strategy.initiatives.form.description_label'),
      descriptionPlaceholder: i18n.t('strategy.initiatives.form.description_placeholder', {
        entity: entityName.toLowerCase(),
      }),
      startDateLabel: i18n.t('strategy.initiatives.form.start_date_label'),
      endDateLabel: i18n.t('strategy.initiatives.form.end_date_label'),
      datePlaceholder: i18n.t('strategy.initiatives.form.date_placeholder'),
      businessValueLabel: i18n.t('strategy.initiatives.form.business_value_label'),
      businessValuePlaceholder: i18n.t('strategy.initiatives.form.business_value_placeholder'),
      numericLabel: i18n.t('strategy.initiatives.form.numeric_label'),
      dollarLabel: currencyName,
      deleteButton: i18n.t('common.actions.delete') + ' ' + entityName,
      cancel: i18n.t('common.actions.cancel'),
      nextStep: i18n.t('strategy.initiatives.form.next_step'),
    };
  };

  const labels = getLabels();

  return (
    <>
      <Stack mih={0}>
        <ScrollArea h={452} scrollbarSize={8}>
          <Stack gap={16} pr={8}>
            <Text>{labels.formDescription}</Text>

            <TextInput
              data-bf="strategy-modal-input-name"
              required
              label={labels.nameLabel}
              placeholder={labels.namePlaceholder}
              maxLength={150}
              {...form.getInputProps('name')}
              {...commonInputProps}
            />

            <Divider />

            <Textarea
              data-bf="strategy-modal-input-description"
              required
              label={labels.descriptionLabel}
              placeholder={labels.descriptionPlaceholder}
              maxLength={1000}
              maxRows={8}
              {...form.getInputProps('description')}
              {...commonInputProps}
              autosize
              classNames={classes}
            />

            <Divider />

            <Group gap={24}>
              <DatePickerInput
                data-bf="strategy-modal-input-start-date"
                label={labels.startDateLabel}
                placeholder={labels.datePlaceholder}
                {...form.getInputProps('start_date')}
                popoverProps={{ zIndex: 600 }}
                {...commonInputProps}
                flex={1}
                rightSection={<Icon name="calendar_month" size={16} color={skyDark} />}
                rightSectionPointerEvents="none"
                h={80}
                valueFormat={STANDARD_DATE_FORMATS.FULL_DATE}
              />
              <DatePickerInput
                data-bf="strategy-modal-input-end-date"
                label={labels.endDateLabel}
                placeholder={labels.datePlaceholder}
                {...form.getInputProps('end_date')}
                popoverProps={{ zIndex: 600 }}
                {...commonInputProps}
                flex={1}
                rightSection={<Icon name="calendar_month" size={16} color={skyDark} />}
                rightSectionPointerEvents="none"
                h={80}
                valueFormat={STANDARD_DATE_FORMATS.FULL_DATE}
              />
            </Group>

            <Group gap={24} h={96}>
              <TextInput
                label={labels.businessValueLabel}
                placeholder={labels.businessValuePlaceholder}
                {...form.getInputProps('relative_business_value')}
                {...commonInputProps}
                flex={1}
                type="number"
                min={0}
                max={999999999}
                style={{
                  alignSelf: 'flex-start',
                }}
              />
              <Group gap={16} flex={1}>
                <Radio
                  label={labels.numericLabel}
                  checked={form.values.relative_business_value_unit === RelativeBusinessValueUnits.numeric}
                  onChange={() =>
                    form.setFieldValue('relative_business_value_unit', RelativeBusinessValueUnits.numeric)
                  }
                />
                <Radio
                  label={labels.dollarLabel}
                  checked={form.values.relative_business_value_unit === RelativeBusinessValueUnits.dollars}
                  onChange={() =>
                    form.setFieldValue('relative_business_value_unit', RelativeBusinessValueUnits.dollars)
                  }
                />
              </Group>
            </Group>
          </Stack>
        </ScrollArea>

        <form onSubmit={form.onSubmit((values) => onNext(values))} style={{ flexShrink: 0 }}>
          <Stack gap={24}>
            <Divider />

            <Group justify="space-between">
              {mode === 'edit' && (
                <Button
                  data-bf="strategy-modal-btn-delete"
                  radius="xl"
                  variant="outline"
                  onClick={() => setIsDeleteModalOpen(true)}
                  leftSection={<Icon name="delete" color={inkLight} size={18} pointer />}
                  style={{ borderColor: redBase }}
                  disabled={isDeleting}
                >
                  {labels.deleteButton}
                </Button>
              )}
              <Group justify="flex-end" gap={16} flex={1}>
                <Button data-bf="strategy-modal-btn-cancel" radius="xl" variant="outline" onClick={onCancel}>
                  {labels.cancel}
                </Button>

                <Button
                  data-bf="strategy-modal-btn-next"
                  type="submit"
                  radius="xl"
                  variant="secondary"
                  rightSection={<Icon name="arrow_forward" color={baseWhite} size={16} pointer />}
                >
                  {labels.nextStep}
                </Button>
              </Group>
            </Group>
          </Stack>
        </form>
      </Stack>

      <StrategyEntityDeleteModal
        opened={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDeleteConfirm}
        isDeleting={isDeleting}
        entityName={form.values.name}
        entityType={entityType}
      />
    </>
  );
}
